<template>
  <!-- 가격안내 카드 편집 모달 -->
  <a-modal
    :visible="visible"
    :footer="null"
    :mask-closable="false"
    :width="560"
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">
        {{ isNew ? '요금제 추가' : '요금제 편집' }}
      </div>
    </template>

    <div class="modal-content">
      <!-- 요금제 이름 (필수) -->
      <div class="field">
        <label class="field__label">요금제 이름 <span class="required">*</span></label>
        <a-input
          v-model:value="form.name"
          :maxlength="30"
          show-count
          placeholder="요금제 이름 (최대 30자)"
        />
      </div>

      <!-- 뱃지 텍스트 -->
      <div class="field">
        <label class="field__label">뱃지 텍스트</label>
        <a-input
          v-model:value="form.badge"
          :maxlength="10"
          show-count
          placeholder="예: 인기, 추천 (최대 10자)"
        />
      </div>

      <!-- 뱃지 색상 -->
      <div v-if="form.badge" class="field field--row">
        <div class="field field--half">
          <label class="field__label">뱃지 배경색</label>
          <div class="color-picker-row">
            <input
              v-model="form.badgeBgColor"
              type="color"
              class="color-input"
            />
            <span class="color-value">{{ form.badgeBgColor }}</span>
          </div>
        </div>
        <div class="field field--half">
          <label class="field__label">뱃지 글자색</label>
          <div class="color-picker-row">
            <input
              v-model="form.badgeTextColor"
              type="color"
              class="color-input"
            />
            <span class="color-value">{{ form.badgeTextColor }}</span>
          </div>
        </div>
      </div>

      <!-- 뱃지 미리보기 -->
      <div v-if="form.badge" class="badge-preview">
        <span
          class="badge-preview__tag"
          :style="{
            backgroundColor: form.badgeBgColor,
            color: form.badgeTextColor,
          }"
        >
          {{ form.badge }}
        </span>
      </div>

      <!-- 설명 -->
      <div class="field">
        <label class="field__label">설명</label>
        <a-textarea
          v-model:value="form.description"
          :maxlength="100"
          show-count
          :rows="2"
          placeholder="요금제 간단 설명 (최대 100자)"
        />
      </div>

      <!-- 가격 + 설치비 -->
      <div class="field field--row">
        <div class="field field--half">
          <label class="field__label">월 가격 (원) <span class="required">*</span></label>
          <a-input-number
            v-model:value="form.price"
            :min="0"
            style="width: 100%;"
            placeholder="0"
            :formatter="(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
            :parser="(v) => v.replace(/,/g, '')"
          />
        </div>
        <div class="field field--half">
          <label class="field__label">설치비 (원)</label>
          <a-input-number
            v-model:value="form.setupFee"
            :min="0"
            style="width: 100%;"
            placeholder="0"
            :formatter="(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
            :parser="(v) => v.replace(/,/g, '')"
          />
        </div>
      </div>

      <!-- 할인율 -->
      <div class="field">
        <label class="field__label">할인율 (%)</label>
        <a-input-number
          v-model:value="form.discount"
          :min="0"
          :max="100"
          style="width: 120px;"
          placeholder="0"
        />
        <span style="margin-left: 8px; color: #888; font-size: 13px;">%</span>
      </div>

      <!-- 기능 목록 (동적 배열) -->
      <div class="field">
        <div class="field__label-row">
          <label class="field__label">포함 기능</label>
          <a-button
            type="link"
            size="small"
            @click="addFeature"
          >
            + 추가
          </a-button>
        </div>
        <div
          v-for="(feat, idx) in form.features"
          :key="idx"
          class="feature-row"
        >
          <a-input
            v-model:value="form.features[idx]"
            placeholder="기능 설명"
            style="flex: 1;"
          />
          <a-button
            type="text"
            danger
            size="small"
            style="margin-left: 8px;"
            @click="removeFeature(idx)"
          >
            삭제
          </a-button>
        </div>
        <div v-if="form.features.length === 0" class="feature-empty">
          기능을 추가하세요.
        </div>
      </div>

      <!-- 활성 여부 -->
      <div class="field field--row field--switch">
        <label class="field__label">활성화</label>
        <a-switch v-model:checked="form.isActive" />
      </div>

      <!-- 모달 푸터 -->
      <div class="modal-footer">
        <!-- 기존 요금제 삭제 버튼 -->
        <a-popconfirm
          v-if="!isNew"
          title="이 요금제를 삭제하시겠습니까?"
          ok-text="삭제"
          cancel-text="취소"
          ok-type="danger"
          @confirm="handleDelete"
        >
          <a-button type="text" danger :loading="loading">삭제</a-button>
        </a-popconfirm>

        <div style="flex: 1;" />

        <a-button @click="handleCancel" :disabled="loading">취소</a-button>
        <a-button
          type="primary"
          class="yellow-button"
          :loading="loading"
          @click="handleSave"
        >
          저장
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { message } from 'ant-design-vue';
import { mapActions, mapMutations } from 'vuex';
import { HomepageApi } from '../../api';

export default {
  name: 'PricingCardEditor',

  props: {
    // 모달 표시 여부
    visible: {
      type: Boolean,
      default: false,
    },
    // 편집할 요금제 (null이면 신규)
    plan: {
      type: Object,
      default: null,
    },
    // 요금제 인덱스
    planIndex: {
      type: Number,
      default: -1,
    },
  },

  emits: ['cancel'],

  data() {
    return {
      loading: false,
      // 로컬 편집 폼 데이터
      form: {
        name: '',
        badge: '',
        badgeBgColor: '#ffd700',
        badgeTextColor: '#333333',
        description: '',
        price: null,
        setupFee: null,
        discount: null,
        features: [],
        isActive: true,
      },
    };
  },

  computed: {
    // 신규 요금제 여부
    isNew() {
      return !this.plan;
    },
  },

  watch: {
    // 모달 열릴 때 폼 데이터 초기화
    visible(val) {
      if (val) {
        this.initForm();
      }
    },
  },

  methods: {
    ...mapActions('homepageEditor', ['loadPageData']),
    ...mapMutations('homepageEditor', ['SET_PAGE_DATA']),

    // 폼 데이터 초기화 (기존 또는 빈 값)
    initForm() {
      if (this.plan) {
        this.form = {
          name: this.plan.name || '',
          badge: this.plan.badge || '',
          badgeBgColor: this.plan.badgeBgColor || '#ffd700',
          badgeTextColor: this.plan.badgeTextColor || '#333333',
          description: this.plan.description || '',
          price: this.plan.price ?? null,
          setupFee: this.plan.setupFee ?? null,
          discount: this.plan.discount ?? null,
          features: Array.isArray(this.plan.features) ? [...this.plan.features] : [],
          isActive: this.plan.isActive !== false,
        };
      } else {
        this.form = {
          name: '',
          badge: '',
          badgeBgColor: '#ffd700',
          badgeTextColor: '#333333',
          description: '',
          price: null,
          setupFee: null,
          discount: null,
          features: [],
          isActive: true,
        };
      }
    },

    // 기능 항목 추가
    addFeature() {
      this.form.features.push('');
    },

    // 기능 항목 제거
    removeFeature(idx) {
      this.form.features.splice(idx, 1);
    },

    // 입력 유효성 검사
    validate() {
      if (!this.form.name || !this.form.name.trim()) {
        message.warning('요금제 이름을 입력해주세요.');
        return false;
      }
      if (this.form.price === null || this.form.price === undefined) {
        message.warning('월 가격을 입력해주세요.');
        return false;
      }
      return true;
    },

    // 저장 처리 (신규/수정)
    async handleSave() {
      if (!this.validate()) return;

      this.loading = true;
      try {
        const payload = {
          ...this.form,
          // 빈 기능 항목 제거
          features: this.form.features.filter((f) => f && f.trim()),
        };

        if (this.isNew) {
          // 신규 생성
          await HomepageApi.createPricingPlan(payload);
          message.success('요금제가 추가되었습니다.');
        } else {
          // 기존 수정
          const id = this.plan._id || this.plan.id;
          await HomepageApi.updatePricingPlan(id, payload);
          message.success('요금제가 수정되었습니다.');
        }

        // pricing 데이터 강제 리로드: null 세팅 후 loadPageData 호출
        this.SET_PAGE_DATA({ page: 'pricing', data: null });
        await this.loadPageData('pricing');

        this.$emit('cancel');
      } catch (error) {
        message.error(error.message || '저장 중 오류가 발생했습니다.');
      } finally {
        this.loading = false;
      }
    },

    // 삭제 처리
    async handleDelete() {
      if (!this.plan) return;

      this.loading = true;
      try {
        const id = this.plan._id || this.plan.id;
        await HomepageApi.deletePricingPlan(id);
        message.success('요금제가 삭제되었습니다.');

        // pricing 데이터 강제 리로드
        this.SET_PAGE_DATA({ page: 'pricing', data: null });
        await this.loadPageData('pricing');

        this.$emit('cancel');
      } catch (error) {
        message.error(error.message || '삭제 중 오류가 발생했습니다.');
      } finally {
        this.loading = false;
      }
    },

    // 취소/닫기
    handleCancel() {
      if (!this.loading) {
        this.$emit('cancel');
      }
    },
  },
};
</script>

<style scoped>
.field {
  margin-bottom: 16px;
}

.field__label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.field__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.field--row {
  display: flex;
  gap: 16px;
}

.field--half {
  flex: 1;
  margin-bottom: 0;
}

.field--switch {
  align-items: center;
  gap: 12px;
}

.field--switch .field__label {
  margin-bottom: 0;
}

.required {
  color: #ff4d4f;
}

.color-picker-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-input {
  width: 40px;
  height: 32px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  padding: 2px;
}

.color-value {
  font-size: 12px;
  color: #888;
}

.badge-preview {
  margin: -8px 0 16px;
}

.badge-preview__tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.feature-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.feature-empty {
  font-size: 13px;
  color: #bbb;
  padding: 8px 0;
}

.modal-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
</style>
